import React, { useEffect, useState } from 'react';
import { useMilanOpenStores, useGlobalData } from '../hooks';
import { useStaticQuery, graphql } from 'gatsby';
import { createMenu } from '../components';
import { updateBorrowedStores } from '.';
import { getMilanGeoCodes } from '../utils';
import { fetchMilanStores } from '../utils/fetchMilanStores';
import { useBuilderPromotionsBannerData } from '../hooks/useBuilderPromotionsBannerData';

export const GlobalSiteDataContext = React.createContext();

const isBrowser = typeof window !== 'undefined';

// Module-level singleton — ensures fetchMilanStores only fires once regardless of
// how many times GlobalContextProvider mounts (Gatsby mounts it in both SSR and browser).
let storesPromise = null;

function getStoresPromise() {
    if (!isBrowser) return null;
    if (!storesPromise) {
        storesPromise = fetchMilanStores();
    }
    return storesPromise;
}

export const GlobalContextProvider = ({ children }) => {
    const localSiteData = useStaticQuery(graphql`
        query LocalSiteData {
            allSitePlugin(filter: { name: { eq: "theme-2023" } }) {
                edges {
                    node {
                        name
                        pluginOptions
                    }
                }
            }
        }
    `).allSitePlugin.edges[0].node.pluginOptions.siteData;

    const [milanStores, setMilanStores] = useState([]);

    useEffect(() => {
        const promise = getStoresPromise();
        if (!promise) return;

        promise
            .then((stores) => setMilanStores(stores))
            .catch((error) => console.error('GlobalContextProvider: failed to load milan stores', error));
    }, []);

    const currentState = milanStores.find((s) => s.name === localSiteData.state);
    // The remote currentMetro from Firebase will overwrite the local one
    const currentMetro = currentState
        ? currentState.metros.find((metro) => metro.name === localSiteData.name)
        : localSiteData;
    const menu = createMenu(currentMetro);
    const milanOpenStores = useMilanOpenStores(milanStores);
    const globalData = useGlobalData();
    const milanGeoCodes = getMilanGeoCodes(milanStores);
    const { promotionalBannersTextData, isLoading: isBuilderDataLoading } = useBuilderPromotionsBannerData();

    const siteData = {
        menu,
        milanStores,
        milanOpenStores,
        currentMetro,
        currentState,
        globalData,
        milanGeoCodes,
        promotionalBannersTextData,
        isBuilderDataLoading,
    };

    useEffect(() => {
        if (
            siteData.currentMetro.borrowed_stores &&
            siteData.currentMetro.borrowed_stores.length !== 0 &&
            milanStores.length !== 0
        ) {
            siteData.currentMetro.borrowed_stores = updateBorrowedStores(siteData.currentMetro, milanStores);
        }
    }, [siteData.currentMetro, milanStores]);

    return <GlobalSiteDataContext.Provider value={siteData}>{children}</GlobalSiteDataContext.Provider>;
};
